import React, { useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';
import type { SeasonalIntroPhase } from '../../hooks/useSeasonalPalette';

/**
 * The halloween "curtain": a translucent overlay with a spider web drawing itself into the
 * bottom-right corner and a spider dropping in on its thread, shown while useSeasonalPalette
 * switches the palette underneath. Pointer-transparent, so it never blocks the lobby.
 */

interface HalloweenIntroProps {
  phase: Exclude<SeasonalIntroPhase, 'idle'>;
}

const WEB_SIZE = 400;
const WEB_SPOKES = 9;
const WEB_RINGS = 7;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const drawIn = keyframes`
  from { stroke-dashoffset: 1; }
  to { stroke-dashoffset: 0; }
`;

const descend = keyframes`
  0% { transform: translateY(-190px); }
  70% { transform: translateY(0); }
  85% { transform: translateY(-14px); }
  100% { transform: translateY(0); }
`;

const Overlay = styled.div.attrs({ className: 'halloween-intro' })<{ $phase: HalloweenIntroProps['phase'] }>`
  position: fixed;
  inset: 0;
  z-index: 10001;
  pointer-events: none;
  overflow: hidden;
  background: radial-gradient(120% 90% at 50% 40%, rgba(12, 8, 20, 0.2), rgba(12, 8, 20, 0.72));
  /* Keyframes picked per phase must go through css\`\` so styled-components injects them. */
  ${(props) =>
    props.$phase === 'out'
      ? css`
          animation: ${fadeOut} 0.9s ease forwards;
        `
      : css`
          animation: ${fadeIn} 0.7s ease forwards;
        `}
`;

const Web = styled.svg`
  position: absolute;
  width: 46vmin;
  height: 46vmin;
  bottom: 0;
  right: 0;
  transform: rotate(180deg);
  fill: none;
  stroke: rgba(239, 231, 214, 0.55);
  stroke-width: 1.2;
  stroke-linecap: round;

  path {
    stroke-dasharray: 1;
    stroke-dashoffset: 1;
    animation: ${drawIn} 1.4s ease-out forwards;
  }

  path[data-rings] {
    animation-delay: 0.35s;
  }
`;

const Thread = styled.div`
  position: absolute;
  top: 0;
  left: 64%;
  height: 190px;
  border-left: 1px solid rgba(239, 231, 214, 0.45);
  animation: ${descend} 2.2s cubic-bezier(0.33, 1, 0.68, 1) forwards;
`;

const Spider = styled.svg`
  position: absolute;
  left: -14px;
  bottom: -19px;
  width: 28px;
  height: 28px;
  fill: #0a0710;
  stroke: rgba(239, 231, 214, 0.5);
  stroke-width: 0.8;
`;

const fmt = (n: number): string => n.toFixed(1);

const buildWeb = (): { spokes: string; rings: string } => {
  const angles = Array.from({ length: WEB_SPOKES }, (_, i) => (Math.PI / 2) * (i / (WEB_SPOKES - 1)));
  const spokes = angles.map((a) => `M0 0 L${fmt(WEB_SIZE * Math.cos(a))} ${fmt(WEB_SIZE * Math.sin(a))}`).join(' ');

  const rings = Array.from({ length: WEB_RINGS }, (_, k) => {
    const radius = (WEB_SIZE * (k + 1)) / (WEB_RINGS + 0.5);
    const sag = radius * 0.92;
    return angles
      .slice(1)
      .map((a, i) => {
        const prev = angles[i] ?? 0;
        const mid = (prev + a) / 2;
        const start = i === 0 ? `M${fmt(radius * Math.cos(prev))} ${fmt(radius * Math.sin(prev))} ` : '';
        return `${start}Q${fmt(sag * Math.cos(mid))} ${fmt(sag * Math.sin(mid))} ${fmt(radius * Math.cos(a))} ${fmt(radius * Math.sin(a))}`;
      })
      .join(' ');
  }).join(' ');

  return { spokes, rings };
};

const HalloweenIntro: React.FC<HalloweenIntroProps> = ({ phase }) => {
  const web = useMemo(buildWeb, []);
  const viewBox = `0 0 ${WEB_SIZE} ${WEB_SIZE}`;

  return (
    <Overlay $phase={phase} aria-hidden="true">
      <Web viewBox={viewBox}>
        <path d={web.spokes} pathLength={1} />
        <path d={web.rings} pathLength={1} data-rings="" />
      </Web>
      <Thread>
        <Spider viewBox="0 0 28 28">
          <path d="M4 6 L11 12 M2 14 L11 14 M3 22 L11 16 M7 26 L12 18 M24 6 L17 12 M26 14 L17 14 M25 22 L17 16 M21 26 L16 18" />
          <ellipse cx="14" cy="10" rx="3.2" ry="3" />
          <ellipse cx="14" cy="17" rx="4.6" ry="5.4" />
          <circle cx="12.8" cy="9.2" r="0.7" fill="#ff8a1f" stroke="none" />
          <circle cx="15.2" cy="9.2" r="0.7" fill="#ff8a1f" stroke="none" />
        </Spider>
      </Thread>
    </Overlay>
  );
};

export default HalloweenIntro;
